/** Compact, accessible entry point for field-level editing. */
import { Pencil } from "lucide-react";
import { type ReactElement, type ReactNode, useId } from "react";

import { Button } from "@/shared/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

/** Keep the displayed value in place while editing in an anchored popover. */
export function InlineEditPopover({
  label,
  open,
  onOpenChange,
  children,
  footer,
}: {
  label: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  footer: ReactNode;
}): ReactElement {
  const titleId = useId();
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground hover:text-foreground pointer-coarse:size-11"
          aria-label={label}
          title={label}
        >
          <Pencil className="size-3.5" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent aria-labelledby={titleId} className="flex flex-col overflow-hidden">
        <h3 id={titleId} className="shrink-0 border-b px-4 py-3 text-sm font-semibold">
          {label}
        </h3>
        <div className="min-h-0 overflow-y-auto overscroll-contain">{children}</div>
        <div className="shrink-0 border-t p-3">{footer}</div>
      </PopoverContent>
    </Popover>
  );
}
